import { AxiosResponse } from 'axios';

import { getProgramDetailsUrl, getVodDetailsUrl } from 'config/api';

import {
    BTVProgrammesResponse,
    ResponseError,
    VodResponse,
} from 'store/railsBuilder/reducer.types';
import { AsyncActionMetaOptions } from 'store/types.ts';

import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import NagraInstance from 'utils/axios/nagra/DefaultInstance';
import { delayRetry } from 'utils/axios/nagra/utils.ts';

import { DETAILS_PARAMS_TO_REQUEST_PARAMS } from 'screens/Detail/types';

import {
    DETAILS_DATA_TYPES,
    getChannelIdFilter,
    getEpgProgramsPeriodFilter,
} from './constants';

type Params = {
    type: DETAILS_DATA_TYPES;
    id: string | undefined;
    channelId?: string | undefined;
} & Pick<
    AsyncActionMetaOptions,
    'abortController' | 'autoRetry' | 'retryCount' | 'retryDuration'
>;

async function requestProgramDetailsWithRetry({
    id,
    channelId,
    autoRetry,
    abortController,
    retryCount = 5,
    retryDuration = 5 * 60 * 1000,
}: Omit<Params, 'type'>): Promise<
    AxiosResponse<BTVProgrammesResponse> | undefined
> {
    try {
        return await NagraInstance.get(
            getProgramDetailsUrl({
                filter: JSON.stringify({
                    [DETAILS_PARAMS_TO_REQUEST_PARAMS.editorialId]: id,
                    ...getChannelIdFilter(channelId),
                    ...getEpgProgramsPeriodFilter(),
                }),
            }),
            { signal: abortController?.signal },
        );
    } catch (error) {
        if (autoRetry && isErrorNeedsRetry(error)) {
            for (let i = 1; i <= retryCount; i += 1) {
                if (abortController?.aborted) {
                    return undefined;
                }

                // eslint-disable-next-line no-await-in-loop
                await delayRetry(i, retryCount, retryDuration);

                try {
                    // eslint-disable-next-line no-await-in-loop
                    return await NagraInstance.get(
                        getProgramDetailsUrl({
                            filter: JSON.stringify({
                                [DETAILS_PARAMS_TO_REQUEST_PARAMS.editorialId]:
                                    id,
                                ...getChannelIdFilter(channelId),
                                ...getEpgProgramsPeriodFilter(),
                            }),
                        }),
                        { signal: abortController?.signal },
                    );
                } catch (retryError) {
                    if (i === retryCount) {
                        throw retryError;
                    }
                }
            }
        }

        return undefined;
    }
}

function requestAssetDetailsData({
    type,
    id,
    channelId,
    autoRetry,
    abortController,
    retryCount,
    retryDuration,
}: Params):
    | Promise<
          | AxiosResponse<BTVProgrammesResponse | VodResponse | ResponseError>
          | undefined
      >
    | undefined {
    if (type === DETAILS_DATA_TYPES.VOD) {
        return NagraInstance.get(
            getVodDetailsUrl({
                filter: JSON.stringify({
                    [DETAILS_PARAMS_TO_REQUEST_PARAMS.editorialId]: id,
                }),
            }),
        );
    }

    if (type === DETAILS_DATA_TYPES.PROGRAM) {
        return requestProgramDetailsWithRetry({
            id,
            channelId,
            autoRetry,
            abortController,
            retryCount,
            retryDuration,
        });
    }

    return undefined;
}

export default requestAssetDetailsData;
